/**
 * LogoLoop
 * KV下のロゴ帯を横方向に無限ループさせる（ライブラリ不使用・CSSアニメ＋最小JS）。
 *
 * 仕組み：トラックの中身を「1ループ単位」に組み、それを2つ並べて CSSで translateX(-50%) を
 * 線形・無限再生する。-50% がちょうど1単位ぶんになるのでシームレス。
 *
 * 重要：translateX(-50%) は「1ループ単位ぶん」しか流れない。1単位がコンテナ幅より狭いと、
 * -50%へ近づくにつれ右端に単位の終端（空白）が見えて途切れる。これを防ぐため、原本アイテムを
 * 「1単位がコンテナ幅以上」になるまで繰り返してから2倍化する（広い画面・90%ズーム等の対策）。
 *
 * 速度：data-loop-speed（px/秒・既定40）。1単位幅 ÷ 速度 = 1周の時間。枚数や複製数に依らず一定。
 *
 * 速度が狂う経路が2つあるので塞いである。
 *   1) 画像が読み込まれる前に測ると、幅が分からず1単位を短く見積もる＝そのぶん速く流れる。
 *      とくに width/height 属性の無い画像（登録物）で起きる。組み直しのたびに img は
 *      作り直されるので、リサイズでも起きる。読み込み後に一度だけ測り直す。
 *   2) 測れなかったとき --loop-duration が未設定のままだと、CSS側の既定30sが効く。
 *      30sは1単位幅と無関係なので、単位が長い画面では大幅に速くなる。直前の値を残して防ぐ。
 * prefers-reduced-motion: reduce のときは複製・アニメをせず静止表示。
 * リサイズ／ズーム変更時は単位幅を取り直して組み直す。
 *
 * @example
 * <div class="p-logos js-logo-loop" data-loop-speed="40">
 *   <ul class="p-logos-track"><li><img …></li>…</ul>
 * </div>
 */
export default class LogoLoop {
  constructor(options = {}) {
    this.selector = options.selector || '.js-logo-loop';
    this.trackSelector = options.trackSelector || '.p-logos-track';
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.roots = Array.from(document.querySelectorAll(this.selector));
    this.roots.forEach((root) => this.setup(root));

    if (!this.reduce && this.roots.length) {
      let timer = null;
      window.addEventListener('resize', () => {
        clearTimeout(timer);
        timer = window.setTimeout(() => this.roots.forEach((root) => {
          root._logoRemeasured = 0;
          this.build(root);
        }), 200);
      });
    }
  }

  /**
   * 初期化：原本マークアップを退避してから組む
   * @param {HTMLElement} root
   */
  setup(root) {
    if (!root) return;

    const track = root.querySelector(this.trackSelector);
    if (!track || !track.children.length) return;

    // 原本（複製前）を保持。組み直しのたびにこれへ戻す
    root._logoOriginalHTML = track.innerHTML;

    // 動きを減らす設定では静止（複製・アニメなし）
    if (this.reduce) return;

    this.build(root);
  }

  /**
   * トラックを組む（初期化・リサイズ共通）。原本へ戻してから単位を作り2倍化する
   * @param {HTMLElement} root
   */
  build(root) {
    const track = root.querySelector(this.trackSelector);
    if (!track || root._logoOriginalHTML == null) return;

    root.classList.remove('is-loop-ready');
    track.style.removeProperty('--loop-duration');
    track.innerHTML = root._logoOriginalHTML;

    const originals = Array.from(track.children);

    // 1ループ単位がコンテナ幅以上になるまで原本を繰り返す（-50%で右に空白が出ないように）
    const containerWidth = root.clientWidth;
    let guard = 0;
    while (track.scrollWidth < containerWidth && guard < 50) {
      originals.forEach((node) => track.appendChild(this.cloneItem(node)));
      guard += 1;
    }

    // ここまでが「1ループ単位」。translateX(-50%) 用に単位ごと2倍化する
    const unitWidth = track.scrollWidth;
    Array.from(track.children).forEach((node) => track.appendChild(this.cloneItem(node)));

    const speed = parseFloat(root.dataset.loopSpeed) || 40;
    if (unitWidth > 0 && speed > 0) {
      root._logoDuration = `${unitWidth / speed}s`;
    }
    // 測れなかったときも直前の値を入れる。未設定だとCSSの既定30sに落ちて速くなる
    if (root._logoDuration) {
      track.style.setProperty('--loop-duration', root._logoDuration);
    }

    root.classList.add('is-loop-ready');

    this.remeasureWhenLoaded(root);
  }

  /**
   * 画像の読み込みを待って測り直す。読み込み前は幅が分からず1単位を短く見積もるため、
   * そのままだと速いまま固定される。組み直すと img は作り直されるので毎回見る。
   * @param {HTMLElement} root
   */
  remeasureWhenLoaded(root) {
    const track = root.querySelector(this.trackSelector);
    if (!track) return;

    const pending = Array.from(track.querySelectorAll('img'))
      .filter((img) => !img.complete || !img.naturalWidth);
    // 測り直しは数回まで。読み込みの取りこぼしで組み直しが続くのを防ぐ
    if (!pending.length || (root._logoRemeasured || 0) >= 3) return;

    root._logoRemeasured = (root._logoRemeasured || 0) + 1;

    let left = pending.length;
    const done = () => {
      left -= 1;
      if (left === 0) this.build(root);
    };
    pending.forEach((img) => {
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  }

  /**
   * ループ複製用のクローン（支援技術からは隠す）
   * @param {HTMLElement} node
   * @returns {HTMLElement}
   */
  cloneItem(node) {
    const clone = node.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    return clone;
  }
}
